import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { adminNavItems, type AdminSection } from "../adminNav";
import type { AdminFlagOverride } from "../flags";
import type { ActPrompt, CostReport, EventStat, PlanTier, User } from "../types";
import { formatTimestamp, formatUsd } from "./helpers";
import { AdminRequests } from "./AdminRequests";

// The users list returns a stable subset of the account shape. Deriving it from
// User (not a hand-copied interface) keeps client and server in lockstep — a
// field rename in types.ts breaks typecheck here too.
type AdminUser = Pick<User, "id" | "email" | "isAdmin" | "plan" | "credits" | "creditsTotal">;
type AdminSqlValue = string | number | boolean | null;
type AdminSqlResult = { columns: string[]; rows: Record<string, AdminSqlValue>[]; truncated: boolean };
type AdminAudioPreview = { name: string; url: string };

const DEFAULT_SQL =
  "select id, name, state, audio_duration_secs, error from transcripts order by created_at desc limit 20";

function sqlCell(value: AdminSqlValue): string {
  return value == null ? "" : String(value);
}

// One entry of GET /api/admin/flags: a catalogue flag with its override rows.
interface AdminFlag {
  key: string;
  description: string;
  defaultValue: boolean;
  overrides: AdminFlagOverride[];
}

// Admin console (gated in App by isAdmin; data endpoints are gated server-side
// too). The subnav routes split the existing admin tools into scan-friendly tabs.
export function AdminView({ section = "users" }: { section?: AdminSection }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [promptMsg, setPromptMsg] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<ActPrompt[]>([]);
  const [busy, setBusy] = useState(false); // one write at a time; blocks double-submits
  const seq = useRef(0); // drop /api/users responses that resolve out of order
  const [stats, setStats] = useState<EventStat[]>([]);
  const [costs, setCosts] = useState<CostReport | null>(null);
  const [costsError, setCostsError] = useState(false);
  const [sql, setSql] = useState(DEFAULT_SQL);
  const [sqlBusy, setSqlBusy] = useState(false);
  const [sqlError, setSqlError] = useState<string | null>(null);
  const [sqlResult, setSqlResult] = useState<AdminSqlResult | null>(null);
  const [audioId, setAudioId] = useState("");
  const [audioBusy, setAudioBusy] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [audioPreview, setAudioPreview] = useState<AdminAudioPreview | null>(null);

  const load = useCallback(async () => {
    const id = ++seq.current;
    try {
      const res = await fetch("/api/users");
      if (id !== seq.current) return; // a newer load() has superseded this one
      if (res.ok) setUsers((await res.json()) as AdminUser[]);
    } catch {
      /* transient */
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    fetch("/api/act-prompts")
      .then((r) => (r.ok ? r.json() : []))
      .then((p: ActPrompt[]) => setPrompts(p))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch("/api/stats")
      .then((r) => (r.ok ? r.json() : []))
      .then((s: EventStat[]) => setStats(s))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch("/api/admin/costs")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("costs"))))
      .then((c: CostReport) => setCosts(c))
      .catch(() => setCostsError(true));
  }, []);

  const [flagData, setFlagData] = useState<{ flags: AdminFlag[]; orphans: AdminFlagOverride[] } | null>(null);
  const loadFlags = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/flags");
      if (res.ok) setFlagData((await res.json()) as { flags: AdminFlag[]; orphans: AdminFlagOverride[] });
    } catch {
      /* transient */
    }
  }, []);
  useEffect(() => {
    void loadFlags();
  }, [loadFlags]);

  const saveOverride = async (key: string, userId: string | null, on: boolean) => {
    if (busy) return;
    setBusy(true);
    try {
      await fetch(`/api/admin/flags/${key}/overrides`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, value: on ? "on" : "off" }),
      }).catch(() => {});
      await loadFlags();
    } finally {
      setBusy(false);
    }
  };

  const removeOverride = async (key: string, userId: string | null) => {
    if (busy) return;
    setBusy(true);
    try {
      await fetch(`/api/admin/flags/${key}/overrides${userId ? `?user=${encodeURIComponent(userId)}` : ""}`, {
        method: "DELETE",
      }).catch(() => {});
      await loadFlags();
    } finally {
      setBusy(false);
    }
  };

  const invite = async (e: FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const res = await fetch("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value }),
      }).catch(() => null);
      // The server creates the account even if the email send fails, and reports
      // which happened via `sent` — so don't claim a link went out when it didn't.
      const data = res && res.ok ? ((await res.json().catch(() => null)) as { sent?: boolean } | null) : null;
      setMsg(
        res && res.ok
          ? data?.sent === false
            ? `Invited ${value}. The account was created, but the sign-in email could not be sent.`
            : `Invited ${value}. A sign-in link was sent.`
          : "Invite failed.",
      );
      setEmail("");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const setPlan = async (id: string, plan: PlanTier) => {
    if (busy) return;
    setBusy(true);
    try {
      await fetch(`/api/users/${id}/plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan }),
      }).catch(() => {});
      await load();
    } finally {
      setBusy(false);
    }
  };

  const addCredits = async (e: FormEvent<HTMLFormElement>, id: string) => {
    e.preventDefault();
    if (busy) return;
    const form = e.currentTarget;
    const credits = Number(new FormData(form).get("credits"));
    if (!Number.isInteger(credits) || credits <= 0) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/users/${id}/credits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credits }),
      }).catch(() => null);
      if (res?.ok) {
        form.reset();
        await load();
      }
    } finally {
      setBusy(false);
    }
  };

  const impersonate = async (id: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/users/${id}/impersonate`, { method: "POST" }).catch(() => null);
      if (res?.ok) {
        window.location.href = "/app"; // the new session cookie is already set
        return;
      }
      setMsg("Could not sign in as that user.");
    } finally {
      setBusy(false);
    }
  };

  const savePrompts = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch("/api/act-prompts", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompts: prompts.map(({ key, prompt }) => ({ key, prompt })) }),
      }).catch(() => null);
      if (res?.ok) {
        setPrompts((await res.json()) as ActPrompt[]);
        setPromptMsg("Saved act prompts.");
      } else {
        setPromptMsg("Could not save act prompts.");
      }
    } finally {
      setBusy(false);
    }
  };

  const runSql = async (e: FormEvent) => {
    e.preventDefault();
    if (sqlBusy) return;
    setSqlBusy(true);
    setSqlError(null);
    try {
      const res = await fetch("/api/admin/sql", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sql }),
      }).catch(() => null);
      const data = res ? await res.json().catch(() => null) : null;
      if (res?.ok) {
        setSqlResult(data as AdminSqlResult);
      } else {
        setSqlResult(null);
        setSqlError((data as { error?: string } | null)?.error ?? "SQL query failed.");
      }
    } finally {
      setSqlBusy(false);
    }
  };

  const loadAudioPreview = async (e: FormEvent) => {
    e.preventDefault();
    const id = audioId.trim();
    if (!id || audioBusy) return;
    setAudioBusy(true);
    setAudioError(null);
    setAudioPreview(null);
    try {
      const res = await fetch(`/api/admin/transcripts/${encodeURIComponent(id)}/audio`).catch(() => null);
      const data = res ? await res.json().catch(() => null) : null;
      if (res?.ok) {
        setAudioPreview(data as AdminAudioPreview);
      } else {
        setAudioError((data as { error?: string } | null)?.error ?? "Could not load audio preview.");
      }
    } finally {
      setAudioBusy(false);
    }
  };

  return (
    <div className="admin-view">
      <header className="admin-head">
        <h1>Admin</h1>
        <a className="admin-back" href="/app">
          ← Back to app
        </a>
      </header>

      <nav className="admin-nav" aria-label="Admin sections">
        {adminNavItems.map((item) => (
          <a key={item.section} href={item.path} className={section === item.section ? "active" : ""} aria-current={section === item.section ? "page" : undefined}>
            {item.label}
          </a>
        ))}
      </nav>

      {section === "users" && (
        <>
          <section className="admin-section">
            <h2>Invite a user</h2>
            <form className="admin-invite" onSubmit={invite}>
              <label className="field">
                <span className="field-label">Invite email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </label>
              <button className="btn-primary" type="submit" disabled={busy}>
                Send invite
              </button>
            </form>
            {msg && <p className="admin-msg">{msg}</p>}
          </section>

          <AdminRequests />

          <section className="admin-section">
            <h2>Users ({users.length})</h2>
            <table className="admin-users">
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Account</th>
                  <th scope="col">Credits</th>
                  <th scope="col" className="au-action">
                    Actions
                  </th>
                  <th scope="col" className="au-signin">
                    Sign in as
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="au-email">
                      {u.email}
                      {u.isAdmin && <span className="au-badge">admin</span>}
                    </td>
                    <td className="au-plan">{u.plan}</td>
                    <td className="au-credits">
                      {u.credits.toLocaleString("en-US")} / {u.creditsTotal.toLocaleString("en-US")}
                    </td>
                    <td className="au-action">
                      <div className="au-actions">
                        <button disabled={busy} onClick={() => void setPlan(u.id, u.plan === "paid" ? "free" : "paid")}>
                          {u.plan === "paid" ? "Make free" : "Make paid"}
                        </button>
                        <form className="au-credit-form" onSubmit={(e) => void addCredits(e, u.id)}>
                          <input
                            name="credits"
                            type="number"
                            min="1"
                            step="1"
                            inputMode="numeric"
                            placeholder="Credits"
                            aria-label={`Credits to add to ${u.email}`}
                            disabled={busy}
                          />
                          <button disabled={busy} type="submit">
                            Add
                          </button>
                        </form>
                      </div>
                    </td>
                    <td className="au-signin">
                      {!u.isAdmin && (
                        <button className="au-impersonate" disabled={busy} onClick={() => void impersonate(u.id)}>
                          impersonate →
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}

      {section === "config" && (
        <section className="admin-section">
          <h2>Act prompts</h2>
          <form className="admin-prompts" onSubmit={savePrompts}>
            {prompts.map((p) => (
              <label key={p.key}>
                <span>
                  {p.glyph} {p.label}
                </span>
                <textarea
                  value={p.prompt}
                  onChange={(e) =>
                    setPrompts((prev) => prev.map((item) => (item.key === p.key ? { ...item, prompt: e.target.value } : item)))
                  }
                  aria-label={`${p.label} prompt`}
                />
              </label>
            ))}
            <button className="btn-primary" type="submit" disabled={busy || prompts.some((p) => !p.prompt.trim())}>
              Save prompts
            </button>
          </form>
          {promptMsg && <p className="admin-msg">{promptMsg}</p>}
        </section>
      )}

      {section === "flags" && (
        <>
          {flagData === null ? (
            <section className="admin-section">
              <h2>Feature flags</h2>
              <p className="admin-msg">Loading flags.</p>
            </section>
          ) : (
            <>
              {flagData.flags.map((f) => (
                <FlagCard key={f.key} flag={f} users={users} busy={busy} onSave={saveOverride} onRemove={removeOverride} />
              ))}
              {flagData.orphans.length > 0 && (
                <section className="admin-section">
                  <h2>Orphan overrides</h2>
                  <p className="admin-msg">Rows for flags no longer in the code catalogue. Safe to delete.</p>
                  <ul className="flag-tree">
                    {flagData.orphans.map((o) => (
                      <li key={`${o.flagKey}:${o.userId ?? "global"}`}>
                        <FlagState on={o.value === "on"} />
                        <span className="flag-target">
                          {o.flagKey} {o.email ? `for ${o.email}` : "for everyone"}
                        </span>
                        <button className="flag-remove" disabled={busy} onClick={() => void removeOverride(o.flagKey, o.userId)}>
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </>
      )}

      {section === "usage" && (
        <>
          <section className="admin-section">
            <h2>Usage</h2>
            {stats.length === 0 ? (
              <p className="admin-msg">No usage recorded yet.</p>
            ) : (
              <table className="admin-users">
                <thead>
                  <tr>
                    <th scope="col">Event</th>
                    <th scope="col">Total</th>
                    <th scope="col">Last 24 hours</th>
                    <th scope="col">Last 7 days</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((s) => (
                    <tr key={s.type}>
                      <td className="au-email">{s.type}</td>
                      <td>{s.total} total</td>
                      <td>{s.last24h} in last 24 hours</td>
                      <td>{s.last7d} in last 7 days</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="admin-section">
            <h2>Costs</h2>
            {costsError ? (
              <p className="admin-msg">Could not load costs.</p>
            ) : !costs ? (
              <p className="admin-msg">Loading costs.</p>
            ) : costs.recordings.length === 0 ? (
              <p className="admin-msg">No recordings yet.</p>
            ) : (
              <>
                <p className="admin-msg">
                  {formatUsd(costs.totals.usd)} total, {formatUsd(costs.thisMonth.usd)} this month. Estimated at{" "}
                  {formatUsd(costs.ratePerHour)} per audio hour (ElevenLabs Scribe), successful transcriptions only.
                </p>
                <p className="admin-msg">By user</p>
                <table className="admin-users">
                  <thead>
                    <tr>
                      <th scope="col">User</th>
                      <th scope="col">Recordings</th>
                      <th scope="col">Minutes</th>
                      <th scope="col">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costs.perUser.map((u) => (
                      <tr key={u.email ?? "unassigned"}>
                        <td className="au-email">{u.email ?? "(unassigned)"}</td>
                        <td>{u.recordings} recordings</td>
                        <td>{u.minutes} min</td>
                        <td>{formatUsd(u.usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="admin-msg">By recording</p>
                <table className="admin-users">
                  <thead>
                    <tr>
                      <th scope="col">Recording</th>
                      <th scope="col">Owner</th>
                      <th scope="col">Duration</th>
                      <th scope="col">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costs.recordings.map((r) => (
                      <tr key={r.id}>
                        <td className="au-email">{r.name}</td>
                        <td>{r.ownerEmail ?? "(unassigned)"}</td>
                        <td>{formatTimestamp(r.durationSecs)}</td>
                        <td>{formatUsd(r.usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </section>
        </>
      )}

      {section === "recordings" && (
        <section className="admin-section">
          <h2>Recordings</h2>
          <form className="admin-audio-form" onSubmit={loadAudioPreview}>
            <label className="field" htmlFor="admin-audio-id">
              <span className="field-label">Audio preview</span>
              <input
                id="admin-audio-id"
                value={audioId}
                onChange={(e) => setAudioId(e.target.value)}
                placeholder="Recording id"
                spellCheck={false}
              />
            </label>
            <button className="btn-primary" type="submit" disabled={audioBusy || !audioId.trim()}>
              {audioBusy ? "Loading" : "Load preview"}
            </button>
          </form>
          {audioError && <p className="admin-msg admin-error">{audioError}</p>}
          {audioPreview && (
            <div className="admin-audio-player">
              <p className="admin-msg">{audioPreview.name}</p>
              <audio controls preload="none" src={audioPreview.url} />
            </div>
          )}
        </section>
      )}

      {section === "sql" && (
        <section className="admin-section admin-sql">
          <h2>SQL</h2>
          <form className="admin-sql-form" onSubmit={runSql}>
            <label htmlFor="admin-sql-query">
              <span className="field-label">Query</span>
              <textarea
                id="admin-sql-query"
                name="sql"
                value={sql}
                onChange={(e) => setSql(e.target.value)}
                spellCheck={false}
                aria-label="Read-only SQL query"
              />
            </label>
            <button className="btn-primary" type="submit" disabled={sqlBusy || !sql.trim()}>
              {sqlBusy ? "Running" : "Run"}
            </button>
          </form>
          {sqlError && <p className="admin-msg admin-error">{sqlError}</p>}
          {sqlResult && (
            <>
              <p className="admin-msg">
                {sqlResult.rows.length} row{sqlResult.rows.length === 1 ? "" : "s"}
                {sqlResult.truncated ? ". More rows available." : "."}
              </p>
              <div className="admin-sql-results">
                {sqlResult.columns.length === 0 ? (
                  <p className="admin-msg">No rows.</p>
                ) : (
                  <table className="admin-users">
                    <thead>
                      <tr>
                        {sqlResult.columns.map((col) => (
                          <th scope="col" key={col}>
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sqlResult.rows.map((row, i) => (
                        <tr key={i}>
                          {sqlResult.columns.map((col) => (
                            <td key={col}>{sqlCell(row[col])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}

function FlagState({ on }: { on: boolean }) {
  return <span className={`flag-state ${on ? "on" : "off"}`}>{on ? "ON" : "OFF"}</span>;
}

// One catalogue flag as a tree: the code default first, then one line per
// override (resolution is most-specific-wins: user beats everyone beats
// default), and a sentence-shaped add form: [ON/OFF] for [target].
function FlagCard({
  flag,
  users,
  busy,
  onSave,
  onRemove,
}: {
  flag: AdminFlag;
  users: AdminUser[];
  busy: boolean;
  onSave: (key: string, userId: string | null, on: boolean) => Promise<void>;
  onRemove: (key: string, userId: string | null) => Promise<void>;
}) {
  const [target, setTarget] = useState(""); // "" = everyone (the global row)
  const [on, setOn] = useState(!flag.defaultValue); // an override usually flips the default

  return (
    <section className="admin-section">
      <h2>{flag.key}</h2>
      <p className="admin-msg">{flag.description}</p>
      <ul className="flag-tree">
        <li className="flag-default">
          <FlagState on={flag.defaultValue} />
          <span className="flag-target">by default (code)</span>
        </li>
        {flag.overrides.map((o) => (
          <li key={o.userId ?? "global"}>
            <FlagState on={o.value === "on"} />
            <span className="flag-target">{o.email ? `for ${o.email}` : "for everyone"}</span>
            <button className="flag-remove" disabled={busy} onClick={() => void onRemove(flag.key, o.userId)}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      <form
        className="flag-add"
        onSubmit={(e) => {
          e.preventDefault();
          void onSave(flag.key, target || null, on);
        }}
      >
        <select value={on ? "on" : "off"} onChange={(e) => setOn(e.target.value === "on")} aria-label="Override value">
          <option value="on">ON</option>
          <option value="off">OFF</option>
        </select>
        <span className="flag-for">for</span>
        <select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Override target">
          <option value="">everyone</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.email}
            </option>
          ))}
        </select>
        <button className="btn-primary" type="submit" disabled={busy}>
          Add override
        </button>
      </form>
    </section>
  );
}
